import b from 'b_';
import { Tabs, Select, Text, SelectEvent } from 'platform-components';
import { POPUP_DIRECTIONS, SELECT_THEMES } from 'platform-components/constants';
import { withRouterPath, InjectedProps as WithRouterPathProps } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useParams } from 'react-router';
import ym from 'react-yandex-metrika';

import { MetrikaParams } from 'common.components/hooks/use-prep-lesson-click';

import { Filter } from '../typings';

//@ts-ignore
import cssConstants from './constants.scss';
import './promo__filters.scss';

interface Props extends WithRouterPathProps {
    filters: Array<Filter>;
    currentFilterId: number;
    metrikaParams: MetrikaParams;
}

interface Context {
    staticUrls: {
        labLibrarySubjectPromo: string;
        deepLabPromoTabSection: string;
    };
}

const MANY_FILTERS = 6;
const filterWidth = parseInt(cssConstants.filterWidth, 10);

const PromoFilters = (props: Props, context: Context) => {
    const { filters, getRouterPath, currentFilterId, history, metrikaParams } = props;
    const { staticUrls } = context;
    const params = useParams<Record<string, string>>();

    const hasManyFilters = filters.length > MANY_FILTERS;

    const callMetrika = useCallback(
        (filterId: number, filterName: string) => {
            ym('reachGoal', 'teacher_vitrina_chose_theme', {
                ...metrikaParams,
                theme_id: filterId,
                umk_name: filterName,
            });
        },
        [metrikaParams]
    );

    const changeFilter = useCallback(
        (filterId: number, filterName: string) => {
            callMetrika(filterId, filterName);

            const path = params.classId ? staticUrls.labLibrarySubjectPromo : staticUrls.deepLabPromoTabSection;
            history.push(getRouterPath(path, { sectionId: filterId }));
        },
        [callMetrika, params, staticUrls, history, getRouterPath]
    );

    const handleTabChange = useCallback(
        (filterId: number | string, filterName: string) => {
            changeFilter(Number(filterId), filterName);
        },
        [changeFilter]
    );

    const handleSelectChange = useCallback(
        ({ target: { value: sectionId } }: SelectEvent<number>) => {
            const filterName = filters.find((filter) => filter.filterId === sectionId)?.filterName || '';

            changeFilter(sectionId, filterName);
        },
        [filters, changeFilter]
    );

    return (
        <div className="promo__filters">
            <Select
                focused={false}
                maxWidth={filterWidth}
                mix={b('promo', 'filters-select', { 'many-filters': hasManyFilters })}
                onSelect={handleSelectChange}
                popupDirection={POPUP_DIRECTIONS.BOTTOM}
                popupMix="promo__filters-select-popup"
                theme={SELECT_THEMES.ROUNDED}
                value={currentFilterId}
            >
                {filters.map(({ filterId, filterName }) => (
                    <Select.Option key={filterId} mix="promo__filters-select-item" value={filterId}>
                        <Text type={Text.TYPE.MARKDOWN}>{filterName}</Text>
                    </Select.Option>
                ))}
            </Select>
            <Tabs mix={b('promo', 'filters-tabs', { 'many-filters': hasManyFilters })} theme={Tabs.THEME.ROUNDED}>
                {filters.map(({ filterId, filterName }) => {
                    return (
                        <Tabs.Tab
                            id={filterId}
                            onTabSelect={(tabId) => handleTabChange(tabId, filterName)}
                            selected={filterId === currentFilterId}
                        >
                            <Text type={Text.TYPE.MARKDOWN}>{filterName}</Text>
                        </Tabs.Tab>
                    );
                })}
            </Tabs>
        </div>
    );
};

PromoFilters.contextTypes = {
    staticUrls: PropTypes.object.isRequired,
};

export default withRouterPath(PromoFilters);
